import React, { useEffect, useState } from "react";
import { BsChevronDown } from "react-icons/bs";
import { FaPlus, FaTimes } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import Switch from "react-switch";
import { editPhoneNumberType } from "../utils/data";
import { useUpdateUserProfile } from "../hooks/updateUserProfile";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

const EditProfilePhoneNumber = ({ userProfile, isLoading, refetch }) => {
  const {
    updateProfile,
    isLoading: isUpdating,
    error,
  } = useUpdateUserProfile();

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [newPhone, setNewPhone] = useState({
    type: "",
    number: "",
    extension: "",
    isPreferred: false,
  });
  const [customLabel, setCustomLabel] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [overflowStatus, setOverflowStatus] = useState(false);
  const [phoneNumberTypeIcon, setPhoneNumberTypeIcon] = useState(false);

  const phoneNumberTypeIconRotate = phoneNumberTypeIcon
    ? "rotate(180deg)"
    : "rotate(0)";


  // Initialize phone numbers from userProfile
  useEffect(() => {
    if (userProfile?.phones?.length) {
      const phones = userProfile.phones.map((p) => ({
        id: p.type,
        type: p.type.charAt(0).toUpperCase() + p.type.slice(1).toLowerCase(),
        number: p.value,
        extension: p.extension || "",
        isPreferred: p.preferred,
      }));
      setPhoneNumbers(phones);
    }
  }, [userProfile]);

  const handlePhoneChange = (id, field, value) => {
    setPhoneNumbers((prev) =>
      prev.map((phone) =>
        phone.id === id ? { ...phone, [field]: value } : phone
      )
    );
  };

  const handlePreferredChange = (id, isPreferred) => {
    setPhoneNumbers((prev) =>
      prev.map((phone) => ({
        ...phone,
        isPreferred: phone.id === id ? isPreferred : false,
      }))
    );
  };

  const handleRemovePhone = (id) => {
    setPhoneNumbers((prev) => prev.filter((phone) => phone.id !== id));
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");

    // Apply formatting: 1-234-567-8900
    if (cleaned.length >= 11) {
      return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(
        4,
        7
      )}-${cleaned.slice(7, 11)}`;
    } else if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
        10
      )}`;
    } else if (cleaned.length >= 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const validatePhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, "");
    return cleaned.length >= 10;
  };

  const handleAddPhone = () => {
    if (!newPhone.number || !newPhone.type) {
      toast.error("Please fill in both phone number and type");
      return;
    }

    if (!validatePhoneNumber(newPhone.number)) {
      toast.error("Please enter a valid phone number (at least 10 digits)");
      return;
    }

    // Check if phone number already exists
    const phoneExists = phoneNumbers.some(
      (phone) => phone.number === newPhone.number
    );
    if (phoneExists) {
      toast.error("This phone number already exists");
      return;
    }

    const phoneType = newPhone.type === "custom" ? customLabel : newPhone.type;

    const newPhoneEntry = {
      id: Date.now().toString(),
      type: phoneType,
      number: newPhone.number,
      extension: newPhone.extension,
      isPreferred: newPhone.isPreferred,
    };

    // If this is set as preferred, unset others
    if (newPhone.isPreferred) {
      setPhoneNumbers((prev) => [
        ...prev.map((phone) => ({ ...phone, isPreferred: false })),
        newPhoneEntry,
      ]);
    } else {
      setPhoneNumbers((prev) => [...prev, newPhoneEntry]);
    }

    // Reset form
    setNewPhone({ type: "", number: "", extension: "", isPreferred: false });
    setCustomLabel("");
    setShowAddForm(false);
    setOverflowStatus(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (phoneNumbers.length === 0) {
      toast.error("At least one phone number is required");
      return;
    }

    // Validate all phone numbers
    const invalidPhones = phoneNumbers.filter(
      (phone) => !validatePhoneNumber(phone.number)
    );
    if (invalidPhones.length > 0) {
      toast.error("Please ensure all phone numbers are valid");
      return;
    }

    try {
      const updatedData = {
        id: userProfile.id,
        phones: phoneNumbers.map((p) => ({
          type: p.type.toLowerCase(),
          value: p.number,
          preferred: p.isPreferred,
          extension: p.extension || null,
        })),
      };

      await updateProfile(updatedData);

      // Suppress toast on first login — FirstLoginProfileLayout shows its own
      const isFirstLogin = window.location.pathname.includes("first_login_profile");
      if (!isFirstLogin) {
        toast.success("Phone numbers updated successfully!");
      }

      // Close modal
      const modal = document.getElementById("editProfilePhoneNumber");
      if (modal) {
        window.$(modal).modal("hide");
        refetch && refetch();
      }
    } catch (error) {
      console.error("Failed to update phone numbers:", error);
      toast.error("Failed to update phone numbers. Please try again.");
    }
  };

  const handlePhoneTypeSelect = (type) => {
    setNewPhone((prev) => ({ ...prev, type }));
  };

  return (
    <div
      className="modal fade"
      id="editProfilePhoneNumber"
      tabIndex="-1"
      aria-labelledby="editProfilePhoneNumber"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="modal-content border-0 overlay-rounded">
            <div className="modal-header mt-14 border-0 pb-2 mb-1">
              <label className="fs-15" id="editProfilePhoneNumber">
                EDIT: Phone Number
              </label>

              <FaTimes
                size={15}
                className="cursor-pointer text_primary"
                data-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body py-0">
              <div className="d-flex justify-content-end">
                <p className="text_primary small-text">
                  Select Preferred Phone Number:
                </p>
              </div>

              {/* Existing Phone Numbers */}
              {phoneNumbers.map((phone, index) => (
                <div
                  key={phone.id}
                  className="d-flex justify-content-between p-0 mb-3"
                >
                  <div className="d-flex small-text">
                    {phoneNumbers.length > 1 && (
                      <div className="d-flex align-items-center">
                        <MdClose
                          className="text_danger cursor-pointer"
                          size={20}
                          onClick={() => handleRemovePhone(phone.id)}
                        />
                      </div>
                    )}
                    <div className="form-group row mb-0 ml-2">
                      <label className="col-md-5 col-form-label">
                        {phone.type} Phone Number:
                      </label>
                      <div className="col-md-5">
                        <input
                          type="text"
                          className="small-text overlay-phone-input"
                          value={phone.number}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            handlePhoneChange(phone.id, "number", formatted);
                          }}
                          placeholder="1-234-567-8900"
                          disabled={isUpdating}
                          required
                        />
                      </div>
                      <div className="col-md-1">
                        <input
                          type="text"
                          className="small-text overlay-extension-input"
                          placeholder="Extension"
                          value={phone.extension}
                          onChange={(e) =>
                            handlePhoneChange(
                              phone.id,
                              "extension",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center small-text">
                    {phone.isPreferred && (
                      <div className="badge_pomme bg_primary f-10 text-light rounded-pill d-flex align-items-center justify-content-center">
                        <span>Preferred Contact Number</span>
                      </div>
                    )}
                    <div className="ml-4">
                      <Switch
                        onChange={(checked) =>
                          handlePreferredChange(phone.id, checked)
                        }
                        checked={phone.isPreferred}
                        onColor="#5D8FA8"
                        onHandleColor="#50a193"
                        handleDiameter={20}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={14}
                        width={35}
                        disabled={isUpdating}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Another Phone Number Button */}
              <div className={`mt-4 ${showAddForm ? "d-none" : ""}`}>
                <button
                  type="button"
                  className="btn btn-link text_primary f-11 text-decoration-none"
                  onClick={() => {
                    setShowAddForm(true);
                    setTimeout(() => {
                      setOverflowStatus(true);
                    }, 400);
                  }}
                >
                  <FaPlus size={10} className="mr-1" /> Add another Phone Number
                </button>
              </div>

              {/* Add New Phone Form */}
              <div
                className={`d-flex justify-content-between p-0 slide-down ${
                  showAddForm ? "showForm" : ""
                } ${overflowStatus ? "overflow-visible" : ""}`}
              >
                <div className="small-text w-100">
                  <div className="d-flex align-items-start align-items-md-center">
                    <MdClose
                      className="text_danger cursor-pointer"
                      size={20}
                      onClick={() => {
                        setShowAddForm(false);
                        setOverflowStatus(false);
                        setNewPhone({
                          type: "",
                          number: "",
                          extension: "",
                          isPreferred: false,
                        });
                        setCustomLabel("");
                      }}
                    />
                  </div>
                  <div className="form-group row mb-0 ml-0 ml-md-2 w-100">
                    <div className="col-12 col-md-6 col-lg-4 mb-2">
                      <div className="dropdown">
                        <button
                          className="btn btn-block border text-left overlay-dropdown-input-small"
                          type="button"
                          id="dropdownMenuButton"
                          data-toggle="dropdown"
                          data-display="static"
                          aria-haspopup="true"
                          aria-expanded="false"
                          onClickCapture={() =>
                            setPhoneNumberTypeIcon(!phoneNumberTypeIcon)
                          }
                        >
                          <div className="d-flex justify-content-between">
                            <small className="small-text">
                              {newPhone.type || "Select type of Phone Number"}
                            </small>
                            <BsChevronDown
                              style={{
                                transform: phoneNumberTypeIconRotate,
                                transition: "all 0.3s linear",
                              }}
                            />
                          </div>
                        </button>
                        <div
                          className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                          aria-labelledby="dropdownMenuButton"
                        >
                          <p className="pl-4 mb-0 pb-2">
                            <small>Choose one type only:</small>
                          </p>
                          {editPhoneNumberType.map((item, key) => (
                            <a
                              key={key}
                              onClick={() => handlePhoneTypeSelect(item.value)}
                              className="dropdown-item activeHover ml-4 wa"
                              href="#"
                            >
                              <small>{item.label}</small>
                            </a>
                          ))}
                          <div className="ml-5 small-text mb-2">
                            <label className="pt-2">Custom label:</label>
                            <div className="pt-1 d-flex">
                              <input
                                type="text"
                                className="small-text overlay-input"
                                placeholder="Insert custom label"
                                value={customLabel}
                                onChange={(e) => setCustomLabel(e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary w-75 h-35 small-text ml-3"
                                onClick={() => {
                                  if (customLabel.trim()) {
                                    setNewPhone((prev) => ({
                                      ...prev,
                                      type: customLabel.trim(),
                                    }));
                                  }
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-4 mb-2">
                      <input
                        type="text"
                        className="small-text overlay-input"
                        placeholder="1-234-567-8900"
                        value={newPhone.number}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setNewPhone((prev) => ({
                            ...prev,
                            number: formatted,
                          }));
                        }}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-2 mb-2">
                      <input
                        type="text"
                        className="small-text overlay-extension-input"
                        placeholder="Extension"
                        value={newPhone.extension}
                        onChange={(e) =>
                          setNewPhone((prev) => ({
                            ...prev,
                            extension: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-2 mb-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 h-35 small-text"
                        onClick={handleAddPhone}
                      >
                        Add
                      </button>
                    </div>
                    <div className="col-12 d-flex align-items-center mb-2 mb-lg-0">
                      <div className="d-flex align-items-center flex-wrap">
                        <Switch
                          onChange={(checked) =>
                            setNewPhone((prev) => ({
                              ...prev,
                              isPreferred: checked,
                            }))
                          }
                          checked={newPhone.isPreferred}
                          onColor="#5D8FA8"
                          onHandleColor="#50a193"
                          handleDiameter={16}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={12}
                          width={30}
                        />
                        <small className="ml-2">Preferred</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between mt-5">
              <button
                type="button"
                className="btn btn_outline_danger rounded overlay-btn"
                data-dismiss="modal"
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-outline-primary rounded overlay-btn"
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfilePhoneNumber;
